import { ListNumbers, LineSegments, Scissors, Receipt } from "@phosphor-icons/react";
import { formatInches } from "@/lib/measure";
import { money } from "@/lib/format";

const polyArea = (pts = []) =>
  Math.abs(pts.reduce((s, [x, y], i) => { const [x2, y2] = pts[(i + 1) % pts.length]; return s + x * y2 - x2 * y; }, 0) / 2);

// Live read-out of what's drawn in this area: pieces, finished edge feet per profile, cut-outs, cost.
export const studioSummary = (area, totals) => {
  const pieces = (area?.pieces || []).filter((p) => !p.panel_kind);
  const edges = {};
  const cutouts = {};
  pieces.forEach((p) => {
    (p.edges || []).forEach((e) => {
      if (e.seam) return;
      const k = e.profile || "Unfinished";
      edges[k] = (edges[k] || 0) + (Number(e.length_in) || 0);
    });
    (p.cutouts || []).forEach((c) => {
      if (c.seam_part) return;
      const k = c.kind || "Cut-out";
      cutouts[k] = (cutouts[k] || 0) + 1;
    });
  });
  return {
    pieces: pieces.map((p) => ({ id: p.id, label: p.label, L: p.length_in, W: p.width_in, sqft: polyArea(p.points) / 144 })),
    edgeFt: Object.entries(edges).map(([k, inches]) => [k, inches / 12]).sort((a, b) => b[1] - a[1]),
    cutouts: Object.entries(cutouts),
    sqft: pieces.reduce((s, p) => s + polyArea(p.points) / 144, 0),
    total: Number(totals?.total) || 0,
    billed: totals?.billed_sqft,
  };
};

const Head = ({ icon: Icon, children, right }) => (
  <div className="flex items-center justify-between">
    <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">
      <Icon size={13} weight="bold" className="text-zinc-500" />
      {children}
    </span>
    {right != null && (
      <span className="font-mono-data text-[11px] tabular-nums text-zinc-500">{right}</span>
    )}
  </div>
);

export const StudioInspector = ({ area, totals, onOpenQuote, onHide }) => {
  const s = studioSummary(area, totals);
  const card = "rounded-xl border border-zinc-800/60 bg-zinc-950/40 p-3.5";
  return (
    <aside
      className="hidden w-72 shrink-0 flex-col gap-4 overflow-y-auto border-l border-zinc-800/70 bg-zinc-900/70 p-5 backdrop-blur-sm xl:flex"
      data-testid="studio-inspector"
    >
      {onHide && (
        <button
          type="button"
          data-testid="studio-inspector-hide"
          onClick={onHide}
          title="Hide the review panel"
          className="-mt-1 -mr-1 self-end rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-zinc-500 transition-colors duration-150 hover:text-zinc-200"
        >
          Hide ›
        </button>
      )}
      <section className={card} data-testid="inspector-pieces">
        <Head icon={ListNumbers} right={`${s.sqft.toFixed(2)} ft²`}>
          Pieces · {s.pieces.length}
        </Head>
        <ul className="mt-3 space-y-2.5">
          {s.pieces.length === 0 && (
            <li className="text-[11px] text-zinc-500">Nothing drawn yet.</li>
          )}
          {s.pieces.map((p) => (
            <li
              key={p.id}
              className="flex items-center justify-between gap-2 text-xs"
              data-testid={`inspector-piece-${p.id}`}
            >
              <span className="truncate font-semibold text-zinc-200">{p.label}</span>
              <span className="font-mono-data shrink-0 tabular-nums text-[11px] text-zinc-400">
                {formatInches(p.L)} × {formatInches(p.W)} · {p.sqft.toFixed(1)}
              </span>
            </li>
          ))}
        </ul>
      </section>
      <section className={card} data-testid="inspector-edges">
        <Head
          icon={LineSegments}
          right={`${s.edgeFt.reduce((a, [, f]) => a + f, 0).toFixed(1)} lf`}
        >
          Finished edge
        </Head>
        <ul className="mt-3 space-y-1.5">
          {s.edgeFt.length === 0 && (
            <li className="text-[11px] text-zinc-500">No finished edges.</li>
          )}
          {s.edgeFt.map(([k, f]) => (
            <li key={k} className="flex items-center justify-between text-xs">
              <span className="text-zinc-300">{k}</span>
              <span className="font-mono-data tabular-nums text-zinc-400">{f.toFixed(1)} lf</span>
            </li>
          ))}
        </ul>
      </section>
      <section className={card} data-testid="inspector-cutouts">
        <Head icon={Scissors} right={s.cutouts.reduce((a, [, n]) => a + n, 0)}>
          Cut-outs
        </Head>
        <ul className="mt-3 space-y-1.5">
          {s.cutouts.length === 0 && (
            <li className="text-[11px] text-zinc-500">No cut-outs.</li>
          )}
          {s.cutouts.map(([k, n]) => (
            <li key={k} className="flex items-center justify-between text-xs">
              <span className="text-zinc-300">{k}</span>
              <span className="font-mono-data tabular-nums text-zinc-400">× {n}</span>
            </li>
          ))}
        </ul>
      </section>
      <section
        className="mt-auto rounded-xl border border-indigo-400/25 bg-indigo-500/10 p-4"
        data-testid="inspector-cost"
      >
        <Head icon={Receipt}>Live quote</Head>
        <div
          className="mt-2 font-mono-data text-2xl font-bold tabular-nums text-white"
          data-testid="inspector-total"
        >
          {money(s.total)}
        </div>
        <div className="text-[11px] text-zinc-400">{s.billed || 0} ft² billed</div>
        {onOpenQuote && (
          <button
            type="button"
            onClick={onOpenQuote}
            data-testid="inspector-open-quote"
            className="mt-3 flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-indigo-300 transition-colors duration-150 hover:text-indigo-200"
          >
            Line items →
          </button>
        )}
      </section>
    </aside>
  );
};
